/**
 * Create Purchase Order From Needs Dialog (#725)
 *
 * Bulk action for the parts-needs cockpit: turns one or more selected rows
 * into a single new purchase order, one line per need, each tagged with
 * `linkedBookingId` so the parts-needed projection can later show it as
 * "ordered"/"arrived" for that specific work order.
 */

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Alert,
  InputAdornment,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';
import { useSuppliers, useWarehouses, type PartsDashboardItem } from '@/hooks/useInventory';
import { eurosToCents } from '@/utils/money';

interface CreatePurchaseOrderFromNeedsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Selected needs with a resolved catalog part — needs without one can't be ordered this way. */
  needs: PartsDashboardItem[];
  onCreate: (data: {
    supplierId: string;
    warehouseId: string;
    lines: { partId: string; quantityOrdered: number; unitPriceCents: number; linkedBookingId: string }[];
  }) => void;
  isLoading?: boolean;
}

export function CreatePurchaseOrderFromNeedsDialog({
  open,
  onClose,
  needs,
  onCreate,
  isLoading,
}: CreatePurchaseOrderFromNeedsDialogProps) {
  const { t } = useTranslation('inventory');
  const { isDirty, markDirty } = useFormDirty(open);

  const suppliersQuery = useSuppliers({ isActive: true });
  const warehousesQuery = useWarehouses();
  const suppliers = useMemo(
    () => (suppliersQuery.data?.pages ?? []).flatMap((page) => page.data),
    [suppliersQuery.data],
  );
  const warehouses = warehousesQuery.data?.data ?? [];

  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [unitPrices, setUnitPrices] = useState<Record<string, number>>({});
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setSupplierId('');
    setWarehouseId(warehouses.find((w) => w.isDefault)?.id ?? warehouses[0]?.id ?? '');
    setQuantities(Object.fromEntries(needs.map((n) => [n.id, n.quantity])));
    setUnitPrices(Object.fromEntries(needs.map((n) => [n.id, n.unitPriceCents])));
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, needs]);

  const totalCents = needs.reduce((sum, n) => sum + (quantities[n.id] ?? 0) * (unitPrices[n.id] ?? 0), 0);

  const handleSubmit = () => {
    if (!supplierId || !warehouseId) {
      setError(t('partsDashboard.createPo.supplierWarehouseRequired'));
      return;
    }
    const lines = needs
      .filter((n): n is PartsDashboardItem & { partId: string } => n.partId !== null)
      .map((n) => ({
        partId: n.partId,
        quantityOrdered: quantities[n.id] ?? n.quantity,
        unitPriceCents: unitPrices[n.id] ?? n.unitPriceCents,
        linkedBookingId: n.bookingId,
      }));
    if (lines.length === 0) {
      setError(t('partsDashboard.createPo.noOrderableLines'));
      return;
    }
    onCreate({ supplierId, warehouseId, lines });
  };

  const unmatchedCount = needs.filter((n) => n.partId === null).length;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('partsDashboard.createPo.title')}
      isDirty={isDirty}
      onSubmit={handleSubmit}
      submitLabel={t('partsDashboard.createPo.confirm')}
      isLoading={isLoading}
      maxWidth="md"
    >
      <DialogContent dividers>
        {unmatchedCount > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('partsDashboard.createPo.unmatchedWarning', { count: unmatchedCount })}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth required sx={{ mb: 2 }}>
          <InputLabel>{t('partForm.supplierLabel')}</InputLabel>
          <Select
            value={supplierId}
            label={t('partForm.supplierLabel')}
            onChange={(e) => { setSupplierId(e.target.value); markDirty(); }}
          >
            {suppliers.map((supplier) => (
              <MenuItem key={supplier.id} value={supplier.id}>{supplier.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth required sx={{ mb: 2 }}>
          <InputLabel>{t('partsDashboard.createPo.warehouseLabel')}</InputLabel>
          <Select
            value={warehouseId}
            label={t('partsDashboard.createPo.warehouseLabel')}
            onChange={(e) => { setWarehouseId(e.target.value); markDirty(); }}
          >
            {warehouses.map((warehouse) => (
              <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('partsDashboard.partColumn')}</TableCell>
              <TableCell align="right">{t('partsDashboard.quantityColumn')}</TableCell>
              <TableCell align="right">{t('partsDashboard.createPo.unitPriceColumn')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {needs.filter((n) => n.partId !== null).map((n) => (
              <TableRow key={n.id}>
                <TableCell>
                  <Typography variant="body2">{n.partName}</Typography>
                  <Typography variant="caption" color="text.secondary">{n.partSku}</Typography>
                </TableCell>
                <TableCell align="right">
                  <TextField
                    type="number"
                    size="small"
                    value={quantities[n.id] ?? n.quantity}
                    onChange={(e) => {
                      setQuantities((prev) => ({ ...prev, [n.id]: parseInt(e.target.value, 10) || 0 }));
                      markDirty();
                    }}
                    sx={{ width: 90 }}
                    slotProps={{ htmlInput: { min: 1 } }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    type="number"
                    size="small"
                    value={((unitPrices[n.id] ?? n.unitPriceCents) / 100).toFixed(2)}
                    onChange={(e) => {
                      const cents = eurosToCents(e.target.value);
                      if (cents !== null) {
                        setUnitPrices((prev) => ({ ...prev, [n.id]: cents }));
                        markDirty();
                      }
                    }}
                    sx={{ width: 110 }}
                    slotProps={{
                      input: { startAdornment: <InputAdornment position="start">€</InputAdornment> },
                      htmlInput: { step: 0.01, min: 0 },
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Typography variant="subtitle2" sx={{ mt: 2, textAlign: 'right' }}>
          {t('partsDashboard.createPo.total', { amount: (totalCents / 100).toFixed(2) })}
        </Typography>
      </DialogContent>
    </FormDialog>
  );
}
